import { useCallback, useContext } from "react";

import { useSWRHooksConfig } from "@touchtech/web-hooks";

import { ROUTES } from "@is/constants";
import { SessionTransitionContext } from "@is/contexts/SessionTransitionContext";
import { useSession, useSetStoreLanguage } from "@is/hooks";
import { useBasketAbandon } from "@is/hooks/basket/useBasketAbandon";
import { useManageApiHeaders } from "@is/hooks/useManageApiHeaders";
import { createBasketUrl, createConfigurationUrl } from "@is/services";
import { withTimeout } from "@is/utils";

const RESTART_AWAIT_TIMEOUT_MS = 1500;

export const useStartNewSearch = () => {
  const swrConfig = useSWRHooksConfig();
  const { setStoreLanguage } = useSetStoreLanguage();
  const { sessionId } = useManageApiHeaders();

  const { resetSession } = useSession();

  const [, abandonBasket] = useBasketAbandon();

  const sessionTransition = useContext(SessionTransitionContext);

  const restartSearch = useCallback(
    async (shouldBeAbandoned = true) => {
      sessionTransition?.startTransition();
      try {
        if (shouldBeAbandoned) {
          await withTimeout(abandonBasket(), RESTART_AWAIT_TIMEOUT_MS);
        }

        const basketUrl = createBasketUrl();
        await swrConfig.mutate([basketUrl, sessionId], undefined, {
          revalidate: false,
        });

        resetSession();
        setStoreLanguage();

        const configurationUrl = createConfigurationUrl();
        await withTimeout(
          swrConfig.mutate(configurationUrl, undefined),
          RESTART_AWAIT_TIMEOUT_MS
        );
      } catch {
        // swallow errors so redirect always fires
      } finally {
        window.location.href = `/instore${ROUTES.EntryView}`;
      }
    },
    [
      swrConfig,
      abandonBasket,
      resetSession,
      setStoreLanguage,
      sessionId,
      sessionTransition,
    ]
  );

  return {
    restartSearch,
  };
};
